import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calculator, Calendar, CheckCircle, ChevronLeft, ChevronRight, Clock, Gauge, MapPin, MapPinned, Navigation, PackageSearch, Receipt, Route, Truck, Wrench } from 'lucide-react';
import { addDays, format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from '@/components/ui/drawer';
import { FieldTechNav } from '@/components/staff/FieldTechNav';
import { FacilityLookupPanel } from '@/components/disposal/FacilityLookupPanel';
import { RouteDayDisposalPlanner } from '@/components/disposal/RouteDayDisposalPlanner';
import { mapsUrl } from '@/lib/formatters';

export function StaffRouteView({ data, actions, helpers }) {
  const { user, viewDate, route, loading, jobs, scheduled, inProgress, completed, incompleteMileageJobIds, gpsMileage } = data;
  const { logout, setViewDate, setShowExpense, setShowMileage, openJob, checkin, startGpsMileage, stopGpsMileage } = actions;
  const { statusColors } = helpers;
  const [showTools, setShowTools] = useState(false);
  const selectedDate = format(viewDate, 'yyyy-MM-dd');
  const handleCalendarChange = (event) => {
    if (!event.target.value) return;
    setViewDate(new Date(`${event.target.value}T12:00:00`));
  };
  const scrollToJobs = () => document.getElementById('staff-jobs-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return (
    <>
      <section className="rounded-b-[1.75rem] bg-gradient-to-br from-[#0EA5E9] via-[#2563EB] to-[#1D4ED8] px-4 pb-5 pt-5 text-white shadow-lg" data-testid="staff-mobile-command-header">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-sky-100" data-testid="staff-mobile-mode-label">Technician mode</p>
            <h1 className="mt-1 text-4xl font-black leading-none tracking-tight font-heading" data-testid="staff-view-title">My Jobs</h1>
            <p className="mt-2 text-base font-semibold text-sky-50" data-testid="staff-view-date-label">{user?.name} · {format(viewDate, 'EEEE, MMM d')}</p>
          </div>
          <button type="button" onClick={() => setShowTools(true)} className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/20 text-white ring-1 ring-white/35 transition-[transform,background-color] duration-150 active:scale-95" data-testid="staff-tools-header-button" aria-label="Open technician tools">
            <Wrench className="h-7 w-7" />
          </button>
        </div>
      </section>

      <div className="px-3 pt-3 sm:px-4">
        <FieldTechNav onLogout={logout} />
      </div>

      <div className="mx-3 mb-4 mt-3 rounded-3xl border border-blue-100 bg-white p-3 shadow-sm sm:mx-4" data-testid="staff-calendar-selector">
        <p className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-blue-700">Select assigned calendar</p>
        <div className="grid grid-cols-[3.5rem_1fr_3.5rem] gap-2" data-testid="staff-date-navigation">
          <Button variant="outline" className="h-14 rounded-2xl border-blue-100 bg-blue-50 text-blue-700" onClick={() => setViewDate(addDays(viewDate, -1))} data-testid="staff-prev-day" aria-label="Previous day"><ChevronLeft className="w-6 h-6" /></Button>
          <input type="date" value={selectedDate} onChange={handleCalendarChange} className="h-14 min-w-0 rounded-2xl border border-blue-100 bg-white px-3 text-center text-base font-black text-zinc-900" data-testid="staff-date-picker" aria-label="Select route date" />
          <Button variant="outline" className="h-14 rounded-2xl border-blue-100 bg-blue-50 text-blue-700" onClick={() => setViewDate(addDays(viewDate, 1))} data-testid="staff-next-day" aria-label="Next day"><ChevronRight className="w-6 h-6" /></Button>
        </div>
        <Button variant="ghost" className="mt-2 h-12 w-full rounded-2xl bg-sky-50 text-sm font-black text-sky-700" onClick={() => setViewDate(new Date())} data-testid="staff-today">Today · {format(viewDate, 'MMM d')}</Button>
      </div>

      <div className="mx-3 mb-4 grid grid-cols-3 gap-2 sm:mx-4" data-testid="staff-summary">
        <Card className="rounded-3xl border border-sky-100 bg-white shadow-sm"><CardContent className="p-3 text-center">
          <p className="text-3xl font-black text-[#007AFF] font-heading" data-testid="staff-summary-upcoming-count">{scheduled.length}</p>
          <p className="text-[10px] font-bold uppercase tracking-wider text-sky-700">Upcoming</p>
        </CardContent></Card>
        <Card className="rounded-3xl border border-amber-100 bg-white shadow-sm"><CardContent className="p-3 text-center">
          <p className="text-3xl font-black text-amber-600 font-heading" data-testid="staff-summary-active-count">{inProgress.length}</p>
          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Active</p>
        </CardContent></Card>
        <Card className="rounded-3xl border border-emerald-100 bg-white shadow-sm"><CardContent className="p-3 text-center">
          <p className="text-3xl font-black text-emerald-600 font-heading" data-testid="staff-summary-completed-count">{completed.length}</p>
          <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Done</p>
        </CardContent></Card>
      </div>

      {route?.maps_url && (
        <a href={route.maps_url} target="_blank" rel="noopener noreferrer"
          className="mx-3 mb-4 flex min-h-[64px] items-center gap-3 rounded-3xl bg-gradient-to-r from-emerald-500 to-teal-500 p-4 text-white shadow-md transition-[transform,background-color] duration-150 active:scale-[0.98] sm:mx-4" data-testid="smart-route-link">
          <Navigation className="w-7 h-7" />
          <div>
            <p className="text-base font-black">Smart Route · {route.total_jobs} stops</p>
            <p className="text-sm text-emerald-50">Open in Google Maps</p>
          </div>
          <ArrowRight className="w-4 h-4 ml-auto" />
        </a>
      )}

      <div className="mx-3 mb-4 grid grid-cols-2 gap-2 sm:mx-4" data-testid="staff-quick-actions">
        <Button className="h-16 flex-1 rounded-3xl bg-violet-600 text-sm font-black text-white hover:bg-violet-700" onClick={() => setShowExpense(true)} data-testid="submit-expense-btn">
          <Receipt className="w-6 h-6 mr-2" /> Expense
        </Button>
        <Button className="h-16 flex-1 rounded-3xl bg-orange-500 text-sm font-black text-white hover:bg-orange-600" onClick={() => setShowMileage(true)} data-testid="submit-mileage-btn">
          <Gauge className="w-6 h-6 mr-2" /> Mileage
        </Button>
      </div>

      <section id="staff-jobs-section" className="scroll-mt-4 px-3 sm:px-4" data-testid="staff-jobs-section">
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-blue-700">Route board</p>
            <h2 className="text-2xl font-black tracking-tight font-heading">Assigned stops</h2>
          </div>
          <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-black text-blue-700" data-testid="staff-route-job-count">{jobs.length} jobs</span>
        </div>
        {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-zinc-200 rounded-md animate-pulse" />)}</div>
      ) : jobs.length === 0 ? (
        <Card className="rounded-3xl border border-blue-100 bg-white shadow-sm"><CardContent className="py-12 text-center">
          <Calendar className="w-12 h-12 text-blue-300 mx-auto mb-2" />
          <p className="text-zinc-600 text-base font-semibold">No jobs assigned for {format(viewDate, 'MMM d')}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="staff-job-list">
          {jobs.map(job => (
            <Card key={job.id} className="overflow-hidden rounded-[1.75rem] border border-blue-100 bg-white shadow-sm" data-testid={`staff-job-${job.id}`}>
              <div className="p-4 transition-[background-color] duration-150 active:bg-blue-50" onClick={() => openJob(job)} role="button" tabIndex={0} data-testid={`staff-job-card-button-${job.id}`} onKeyDown={(event) => { if (event.key === 'Enter') openJob(job); }}>
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0 pr-2">
                    <p className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-blue-600" data-testid={`staff-job-time-${job.id}`}>{job.scheduled_time_start || 'Any time'}</p>
                    <h3 className="mt-1 text-xl font-black leading-tight text-zinc-900 font-heading" data-testid={`staff-job-title-${job.id}`}>{job.title}</h3>
                    <div className="flex items-center gap-2 text-xs text-zinc-500 mt-0.5">
                      {job.scheduled_time_start && <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{job.scheduled_time_start}</span>}
                    </div>
                  </div>
                  <Badge variant="secondary" className={`shrink-0 text-xs ${statusColors[job.status] || ''}`} data-testid={`staff-job-status-${job.id}`}>{job.status?.replace('_', ' ')}</Badge>
                </div>
                {incompleteMileageJobIds?.has(job.id) && (
                  <div className="mb-2 inline-flex items-center gap-1 rounded-sm border border-sky-200 bg-sky-50 px-2 py-1 text-[11px] font-bold text-sky-700" data-testid={`staff-job-mileage-progress-${job.id}`}>
                    <Gauge className="h-3 w-3" /> Mileage in progress
                  </div>
                )}
                {gpsMileage?.jobId === job.id && gpsMileage?.active && (
                  <div className="mb-2 inline-flex items-center gap-1 rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-700" data-testid={`staff-job-gps-mileage-active-${job.id}`}>
                    <Navigation className="h-3 w-3" /> GPS {Number(gpsMileage.distance || 0).toFixed(2)} mi
                  </div>
                )}
                {job.address && <p className="mb-3 flex items-start gap-2 text-base font-semibold leading-snug text-zinc-700" data-testid={`staff-job-address-${job.id}`}><MapPin className="mt-0.5 h-5 w-5 shrink-0 text-blue-500" />{job.address}<a href={mapsUrl(job.address)} target="_blank" rel="noopener noreferrer" className="ml-1 text-blue-600 hover:text-emerald-700" data-testid={`staff-job-map-link-${job.id}`} aria-label="Open job address in Google Maps">↗</a></p>}
                {job.payment_status && (
                  <div className={`mb-2 rounded-md border px-2 py-1.5 text-xs ${job.payment_status.is_prepaid ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-800'}`} data-testid={`staff-job-payment-status-${job.id}`}>
                    {job.payment_status.is_prepaid ? 'No payment required' : `$${Number(job.payment_status.balance_due || 0).toFixed(2)} balance due`}
                  </div>
                )}

                {/* Action buttons based on status */}
                <div className="mt-3 grid grid-cols-2 gap-2" onClick={(e) => e.stopPropagation()}>
                  {(job.status === 'scheduled' || job.status === 'tentative') && (
                    <Button className="h-16 rounded-2xl bg-[#007AFF] text-base font-black hover:bg-[#0056b3]"
                      onClick={() => { openJob(job); }} data-testid={`start-job-${job.id}`}>
                      <Truck className="w-6 h-6 mr-2" /> Job Details
                    </Button>
                  )}
                  {job.status === 'in_progress' && !job.on_site_at && (
                    <Button className="h-16 rounded-2xl bg-[#007AFF] text-base font-black hover:bg-[#0056b3]"
                      onClick={() => checkin(job.id, 'on_site')} data-testid={`onsite-job-${job.id}`}>
                      <MapPinned className="w-6 h-6 mr-2" /> Arrived
                    </Button>
                  )}
                  {job.status === 'in_progress' && (
                    <Button className="h-16 rounded-2xl bg-emerald-600 text-base font-black hover:bg-emerald-700"
                      onClick={() => openJob(job)} data-testid={`complete-job-${job.id}`}>
                      <CheckCircle className="w-6 h-6 mr-2" /> Details
                    </Button>
                  )}
                  {job.status === 'in_progress' && (
                    <Button className="h-16 rounded-2xl bg-orange-500 text-base font-black text-white hover:bg-orange-600" onClick={() => gpsMileage?.jobId === job.id && gpsMileage?.active ? stopGpsMileage() : startGpsMileage(job)} data-testid={`gps-mileage-toggle-${job.id}`}>
                      <Navigation className="w-6 h-6 mr-2" /> {gpsMileage?.jobId === job.id && gpsMileage?.active ? 'Stop Miles' : 'Start Miles'}
                    </Button>
                  )}
                  {job.address && (
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.address)}`} target="_blank" rel="noopener noreferrer">
                      <Button className="h-16 min-w-16 rounded-2xl bg-indigo-500 text-white hover:bg-indigo-600" data-testid={`staff-job-directions-button-${job.id}`}><Navigation className="w-7 h-7" /></Button>
                    </a>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      </section>

      <Drawer open={showTools} onOpenChange={setShowTools}>
        <DrawerContent className="max-h-[88dvh] overflow-y-auto rounded-t-lg bg-white" data-testid="staff-tools-drawer">
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-heading text-2xl font-black">Route tools</DrawerTitle>
            <DrawerDescription>Disposal lookup, route planning, and quick field utilities.</DrawerDescription>
          </DrawerHeader>
          <div className="space-y-4 px-4 pb-6">
            {route?.maps_url && (
              <a href={route.maps_url} target="_blank" rel="noopener noreferrer" className="flex min-h-[52px] items-center gap-3 border border-zinc-950 bg-zinc-950 px-3 py-2 text-sm font-bold text-white" data-testid="staff-tools-route-link">
                <Route className="h-4 w-4" /> Open smart route <ArrowRight className="ml-auto h-4 w-4" />
              </a>
            )}
            <div data-testid="staff-route-day-disposal-planner-section">
              <RouteDayDisposalPlanner compact defaultDate={format(viewDate, 'yyyy-MM-dd')} testId="staff-route-day-disposal-planner" />
            </div>
            <div data-testid="staff-disposal-lookup-section">
              <FacilityLookupPanel compact testId="staff-disposal-lookup" />
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-zinc-200 bg-white/95 px-2 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-2 backdrop-blur sm:hidden" data-testid="staff-mobile-bottom-nav">
        <div className="grid grid-cols-5 gap-1">
          <button type="button" onClick={scrollToJobs} className="flex min-h-[56px] flex-col items-center justify-center gap-1 text-[11px] font-bold text-[#007AFF]" data-testid="staff-bottom-jobs-button"><Truck className="h-5 w-5" />Jobs</button>
          <Link to="/mobile-estimator" className="flex min-h-[56px] flex-col items-center justify-center gap-1 text-[11px] font-bold text-zinc-600" data-testid="staff-bottom-estimator-link"><Calculator className="h-5 w-5" />Estimate</Link>
          <button type="button" onClick={() => setShowMileage(true)} className="flex min-h-[56px] flex-col items-center justify-center gap-1 text-[11px] font-bold text-zinc-600" data-testid="staff-bottom-mileage-button"><Gauge className="h-5 w-5" />Mileage</button>
          <button type="button" onClick={() => setShowExpense(true)} className="flex min-h-[56px] flex-col items-center justify-center gap-1 text-[11px] font-bold text-zinc-600" data-testid="staff-bottom-expense-button"><Receipt className="h-5 w-5" />Expense</button>
          <button type="button" onClick={() => setShowTools(true)} className="flex min-h-[56px] flex-col items-center justify-center gap-1 text-[11px] font-bold text-zinc-600" data-testid="staff-bottom-tools-button"><PackageSearch className="h-5 w-5" />Tools</button>
        </div>
      </nav>
    </>
  );
}
